import React, { useContext, useEffect, useLayoutEffect, useState } from 'react';
import styled from 'styled-components';
import { FormContext } from './Form';
import useDebounce from '../utility/debounce';
import Heading from './Heading';
import Icon from './Icon';
import TooltipHelper from './TooltipHelper';
import { useGlobalState } from '../stateProvider/StateProvider';
import Row from '../layout/Row';

const Wrapper = styled.div`
    display: ${({ showIf }) => showIf ? 'flex' : 'none'};

    flex-direction: ${({ direction }) => direction};
    position: relative;
    flex: ${({ flex }) => flex};
`;

const InputCSS = styled.input`
    display: flex;
    align-items: center;
    // justify-content: center;
    gap: 0.2em;
    border: 1px solid ${({ value, theme }) => value ? theme.gray : `${theme.gray}80`};
    border-radius: 0.4rem;
    user-select: none;
    color: ${({ theme }) => theme.text};
    transition: background-color, transform, 0.2s;
    background-color: ${({ theme }) => theme.input.background};
    padding: 0.3em 0.5em;
    font-size: inherit;

    padding-left: ${({ inputType }) => inputType === 'price' ? '1.2em' : ''};

    overscroll-behavior: unset;

    &:disabled {
        color: ${({ theme }) => theme.input.disabledText};
        background-color: ${({ theme }) => theme.input.disabled};
        transform: unset;
        box-shadow: unset;
        cursor: default;
    }
    &:not(:disabled) {
        box-shadow: ${({ callToAction, value, theme }) => callToAction && !value ? `0px 0px 2px 0px ${theme.input.active}` : ''};
    }
    
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    &::-webkit-calendar-picker-indicator {
        color-scheme: ${({ darkTheme }) => darkTheme ? 'dark' : 'light'};
    }

    &::placeholder {
        color: ${({ theme }) => theme.input.disabled};
    }
    &:focus {
        outline: none !important;
        border: 1px solid ${({ theme }) => theme.text};
        // box-shadow: 0 0 3px ${({ theme }) => theme.highlight};
    }
`;

const Tag = styled.span`
    font-size: 0.6em;
`;

export const Error = styled(Tag)`
    font-size: 0.5em;
    color: ${({ theme }) => theme.error};
`;

export const OptionalFlag = styled.i`
    color: ${({ theme }) => theme.gray};
    font-weight: 500;
    font-size: 0.8em;
    white-space: nowrap;
`;

const ShowPassword = styled(Icon)`
    position: absolute;
    top: 50%;
    right: 0.5em;
    cursor: pointer;
    color: ${({ theme, disabled }) => disabled ? theme.input.disabledText : theme.text};
`;
const DollarSign = styled(Icon)`
    position: absolute;
    top: 50%;
    left: 0.5em;
    color: ${({ theme, disabled }) => disabled ? theme.input.disabledText : theme.text};
`;

/**
 * Input component for forms.
 * @param {Object} props - Component props.
 * @param {Object} props.context - Context object containing form state and methods.
 * @param {boolean} props.standalone - Indicates whether the component is standalone or part of a context.
 * @param {string} props.type - Input type (e.g., "text", "password", "date").
 * @param {string} props.name - Input name.
 * @param {number} props.debounce - Debounce duration in milliseconds for input value changes.
 * @param {boolean} props.row - Specifies if the input should be displayed in a row.
 * @param {string} props.flex - Flex property value for styling.
 * @param {string} props.label - Input label.
 * @param {string} props.confirmation - Name of the input field to confirm against.
 * @param {boolean} props.required - Indicates whether the input is required.
 * @param {boolean} props.disable - Indicates whether the input is disabled.
 * @param {boolean} [props.showIf=true] - If true, render input field.
 * @param {boolean} props.clamp - Indicates whether the input value should be clamped within a range.
 * @param {string} props.min - Minimum value for clamping.
 * @param {string} props.max - Maximum value for clamping.
 * @param {Function} props.sanitize - Function to sanitize input value.
 * @param {RegExp|string} props.validate - Validation criteria for the input.
 * @param {string} props.invalidMessage - Message to display when input is invalid.
 * @param {boolean} props.disableError - Indicates whether to disable error state.
 * @param {boolean} props.disableErrorMessage - Indicates whether to disable error message display.
 * @param {Function} props.callback - Callback function to be executed on input value change.
 * @returns {React.Component} - Input component.
 */
const Input = ({ context, ...props }) => {
    const {
        state: {
            dark_theme
        },
    } = useGlobalState();

    const [invalid, setInvalid] = useState(false);
    const [invalidMessage, setInvalidMessage] = useState('');
    const [password, setPassword] = useState(props.type === 'password' ? true : null);
    const debouncedValue = useDebounce(context?.values[props.name], props.debounce);

    const [hasTouched, setHasTouched] = useState(false);

    useLayoutEffect(() => {
        if (!context || !context.values[props.name]) return;
        const { values } = context;
        const value = values[props.name];
        if (props.type === 'date' && value.includes('T')) {
            context.setValues(v => ({
                ...v,
                [props.name]: value.substring(0, value.indexOf('T'))
            }));
        } else if (props.type === 'price' && typeof value === 'number') {
            context.setValue(props.name, value.toFixed(2));
        }

        if (!props.confirmation) return;
        const matching = values[props.confirmation] !== values[props.name]
        setInvalidMessage(matching ? 'Must Match.' : '')
        setInvalidation(matching);
    }, [context?.values])

    useEffect(() => {
        if (!context) return;
        validate(debouncedValue);
        if (props.callback)
            props.callback(debouncedValue)
    }, [debouncedValue, props.required])

    useEffect(() => {
        if (!context) return;
        const { values } = context;
        if (props.disable) {
            setInvalidation(false);
        } else {
            validate(values[props.name]);
        }
    }, [props.disable])

    const handleInput = (e) => {
        e = e.target.value;

        if (props.clamp) {
            e = e < props.min ? props.min : e;
            e = e > props.max ? props.max : e;
        }

        const newValue = props.sanitize
            ? typeof props.sanitize === 'function'
                ? props.sanitize(e)
                : e.replace(props.sanitize, '')
            : e;

        context.setValues(v => ({
            ...v,
            [props.name]: newValue
        }))
    }

    const validate = async (val) => {
        let invalid = false;

        if (val === '' && props.required && !props.disabled) {
            if (hasTouched) setInvalidMessage('Please complete this field.');
            invalid = true;
        }

        if (props.validate && val) {
            if (Array.isArray(props.validate)) {
                props.validate.forEach(e => {
                    if (!invalid) {
                        invalid = !(val.defaultMatch(e.regex) !== '');
                        if (invalid) setInvalidMessage(e.message);
                    }
                })
            } else if (typeof props.validate === 'function') {
                invalid = await new Promise((resolve) => props.validate(val, setInvalidMessage, resolve))
            } else {
                invalid = !(val.defaultMatch(props.validate) === val);
            }
        };

        if (context && props.confirmation && val) {
            setInvalidMessage('Must Match.');
            invalid = context.values[props.confirmation] !== val;
        }

        if (invalid && props.invalidMessage) {
            setInvalidMessage(props.invalidMessage);
        }
        if (props.disableError) {
            invalid = false;
        }
        if (props.disableErrorMessage) {
            setInvalidMessage('')
        }

        setInvalidation(invalid)
    }

    const setInvalidation = (invalid) => {
        setInvalid(invalid);

        if (!context) return;
        context.setErrors(v => ({
            ...v,
            [props.name]: invalid
        }));
    }

    if (!password && props.type === 'password') {
        props.type = 'text'
    }

    return props.standalone ? (
        <Wrapper direction={props.row === true ? 'row' : 'column'} flex={props.flex || 1} showIf={props.showIf ?? true}>
            <Row style={{ gap: 0, justifyContent: 'space-between' }} wrap>
                <Heading fontSize={0.8} >
                    {props.label}
                    {context && <TooltipHelper formName={context.formName} name={props.name} />}
                    {context && !props.required && !(context.submitting || context.editing || props.disable) && <OptionalFlag>- Optional</OptionalFlag>}
                </Heading>
                {invalid && <Error>{invalidMessage || props.error}</Error>}
            </Row>

            <InputCSS
                darkTheme={dark_theme}
                value={(context ? `${context.values[props.name] ?? ''}` : props.value) || ''}
                onInput={context && handleInput}
                disabled={context ? context.submitting || context.editing || props.disable : props.disable}
                onFocus={() => setHasTouched(true)}
                onBlur={() => validate((context ? `${context.values[props.name] ?? props.value ?? ''}` : props.value) || '')}
                inputType={props.type}
                callToAction={!hasTouched && props.required}
                {...props}
                type={props.type === 'price' ? 'number' : props.type}
            />
            {password !== null && (
                <ShowPassword
                    icon={password ? 'eye' : 'crossEye'}
                    onClick={() => setPassword(!password)}
                    disabled={context ? context.submitting || context.editing || props.disable : props.disable}
                />
            )}
            {props.type === 'price' && <DollarSign icon='dollarSign' />}
        </Wrapper>
    ) : (
        <InputContext
            standalone={1}
            {...props}
        />
    );
}

export default Input;

const InputContext = ({ ...props }) => {
    const context = useContext(FormContext);
    return (
        <Input
            context={{ ...context }}
            {...props}
        />
    )
}
